// apps/dashboard/src/api/hooks/useCreateWorkspace.ts
//
// Mutation hook for the "New workspace" action on the Workspaces screen.
// Wraps api.repositories.createWorkspace (auth-gated; also enrols the caller
// as an "owner" member so the workspace shows up in list mode).
import { useMutation } from "convex/react";
import { ConvexError } from "convex/values";
import { useCallback, useState } from "react";
import { api } from "../../../../../convex/_generated/api";

export interface CreateWorkspaceError {
  code: string;
  message?: string;
}

export interface CreateWorkspaceResult {
  workspaceId: string;
  slug: string;
}

export function useCreateWorkspace() {
  const mutate = useMutation(api.repositories.createWorkspace);

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<CreateWorkspaceError | null>(null);

  const create = useCallback(
    async (name: string): Promise<CreateWorkspaceResult | null> => {
      setIsCreating(true);
      setError(null);
      try {
        const result = await mutate({ name });
        return result as CreateWorkspaceResult;
      } catch (err: unknown) {
        if (err instanceof ConvexError) {
          setError({
            code: (err.data?.code as string) ?? "UNKNOWN",
            message: err.data?.message as string | undefined,
          });
        } else {
          const message =
            err instanceof Error && err.message
              ? err.message
              : "Network error. Please try again.";
          setError({ code: "UNKNOWN", message });
        }
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [mutate],
  );

  return { create, isCreating, error };
}

export default useCreateWorkspace;
